import { Check, Disc3 } from "lucide-react";
import MixSlider from "@/components/creator/mix/MixSlider";
import { fmtDb } from "@/lib/mixDeskApi";

const KEYS = ["lufs", "warmth", "brightness", "width", "ceiling"];
const tweaked = (m, p) => p && p.key !== "off" && KEYS.some((k) => Math.abs(m[k] - p[k]) > 0.01);

function PresetCard({ p, active, onPick }) {
  return (
    <button type="button" role="radio" aria-checked={active} data-testid={`mix-preset-${p.key}`} onClick={() => onPick(p.key)}
      className={`group rounded-2xl border p-3.5 text-left transition-[background-color,border-color,transform] duration-200 hover:-translate-y-0.5 ${active ? "border-amber-300/60 bg-amber-500/[0.14]" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.07]"}`}>
      <p className="flex items-center justify-between gap-2 font-display text-[14px] font-semibold">
        {p.label}
        {active ? <Check className="h-4 w-4 text-amber-200" /> : null}
      </p>
      <p className="mt-1 text-[11px] leading-snug text-white/55">{p.hint}</p>
      {p.key !== "off" ? <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-white/40">{p.lufs} LUFS</p> : null}
    </button>
  );
}

/** Mastering: one-tap sound presets, then fine-tune loudness, warmth, brightness, width and the peak ceiling. */
export default function MasterSection({ master, presets, onChange }) {
  const preset = presets.find((p) => p.key === master.preset);
  const off = master.preset === "off";
  return (
    <section data-testid="mix-master-section" className="rounded-3xl border border-white/[0.08] bg-[#0a0d1c]/80 p-5">
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em] text-amber-200/90"><Disc3 className="h-3.5 w-3.5" /> Mastering</p>
      <h2 className="mt-2 font-display text-lg font-semibold">
        How should the final song sound?
        {tweaked(master, preset) ? <span data-testid="mix-preset-tuned" className="ml-2 rounded-full border border-amber-300/40 px-2 py-0.5 align-middle font-mono text-[9px] uppercase tracking-wider text-amber-200">Tuned</span> : null}
      </h2>
      <div role="radiogroup" className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {presets.map((p) => <PresetCard key={p.key} p={p} active={p.key === master.preset} onPick={(key) => onChange({ preset: key })} />)}
      </div>
      <div className="mt-6 grid gap-x-6 gap-y-4 sm:grid-cols-2">
        <MixSlider id="mix-master-lufs" label="Loudness" value={master.lufs} min={-20} max={-8} step={0.5} fmt={(v) => `${v.toFixed(1)} LUFS`} onChange={(v) => onChange({ lufs: v })} disabled={off} resetTo={preset?.lufs ?? -14} accent="accent-amber-300" />
        <MixSlider id="mix-master-warmth" label="Warmth" value={master.warmth} min={-6} max={6} step={0.5} fmt={fmtDb} onChange={(v) => onChange({ warmth: v })} disabled={off} resetTo={preset?.warmth ?? 0} accent="accent-amber-300" />
        <MixSlider id="mix-master-brightness" label="Brightness" value={master.brightness} min={-6} max={6} step={0.5} fmt={fmtDb} onChange={(v) => onChange({ brightness: v })} disabled={off} resetTo={preset?.brightness ?? 0} accent="accent-amber-300" />
        <MixSlider id="mix-master-width" label="Stereo width" value={master.width} min={50} max={150} step={5} fmt={(v) => `${Math.round(v)}%`} onChange={(v) => onChange({ width: v })} disabled={off} resetTo={preset?.width ?? 100} accent="accent-amber-300" />
        <MixSlider id="mix-master-ceiling" label="Peak ceiling" value={master.ceiling} min={-3} max={-0.3} step={0.1} fmt={(v) => `${v.toFixed(1)} dB`} onChange={(v) => onChange({ ceiling: v })} disabled={off} resetTo={preset?.ceiling ?? -1} accent="accent-amber-300" />
      </div>
      <p className="mt-4 text-[11px] leading-relaxed text-white/45">
        Warmth, brightness and width play live in the preview. Loudness is matched exactly when you render — streaming services play around −14 LUFS; radio and car play louder.
      </p>
    </section>
  );
}
